/**
 * IVA para los importes «con IVA» de la card de suscripción.
 *
 * La tarifa es neta (`tax_behavior=exclusive` en Stripe) y la card enseña al
 * lado el importe con impuestos. El tipo depende del país del visitante: con un
 * 21 % fijo, a un cliente de Portugal (23 %) le anunciaríamos un total que no es
 * el que luego le cobra el Checkout.
 *
 * El país es el de `pricing.displayCurrency.country`, que el backend resuelve por
 * IP + zona horaria (`billing/local_currency.py`). Es el mismo país con el que se
 * decide la divisa, así que la card no puede decir dos países distintos.
 *
 * Es una ESTIMACIÓN para pintar, no lo que se factura: el IVA real lo calcula
 * Stripe con la dirección de facturación (y a una empresa intracomunitaria con
 * NIF-IVA no se le cobra). Por eso fuera de la UE no se inventa ningún tipo y la
 * línea «con IVA» simplemente no se pinta.
 */

/** Tipo general de IVA por país de la UE, en %. */
export const VAT_RATES = {
  AT: 20, BE: 21, BG: 20, CY: 19, CZ: 21, DE: 19, DK: 25, EE: 24,
  ES: 21, FI: 25.5, FR: 20, GR: 24, HR: 25, HU: 27, IE: 23, IT: 22,
  LT: 21, LU: 17, LV: 21, MT: 18, NL: 21, PL: 23, PT: 23, RO: 21,
  SE: 25, SI: 22, SK: 23,
};

/** País por defecto cuando el backend no sabe de dónde viene el visitante. */
const DEFAULT_COUNTRY = 'ES';

/**
 * Tipo de IVA (en %) para un país, o `null` si no aplica.
 *
 * - País vacío o desconocido para el backend → el de España, que es el mercado
 *   base y lo que decía la card hasta ahora.
 * - País fuera de la UE → `null`: no hay IVA europeo que anunciar.
 *
 * @param {string | null | undefined} country  ISO 3166-1 alfa-2
 * @returns {number | null}
 */
export function vatRateFor(country) {
  const code = String(country || '').trim().toUpperCase() || DEFAULT_COUNTRY;
  return Object.prototype.hasOwnProperty.call(VAT_RATES, code) ? VAT_RATES[code] : null;
}

/**
 * Céntimos con IVA, redondeados al céntimo. `null` si el tipo no aplica.
 *
 * @param {number} cents  importe neto en céntimos
 * @param {number | null} rate  % de `vatRateFor`
 */
export function withVat(cents, rate) {
  if (rate == null) return null;
  return Math.round((Number(cents) || 0) * (100 + rate) / 100);
}
